import React from 'react';

import { UxContext } from '@client/context/UxContext';
import { usePageStore } from '@client/store/pageStore/context/pageContext';
import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import { SpectralRanges, SpectrogramPoint } from '@ledium/types';
import { generateUniqueName } from '@ledium/ui-common';

interface LightRecipeItem {
    id: string;
    name: string;
    description?: string;
    spectrogram?: SpectrogramPoint[];
    spectrogramRanges?: { pfd: SpectralRanges; irradiance: SpectralRanges };
}

function spectrogramsMatch(a: SpectrogramPoint[], b: SpectrogramPoint[]): boolean {
    if (a.length !== b.length) {
        return false;
    }
    return a.every((pointA, i) => {
        const pointB = b[i];
        return pointA.waveLength === pointB.waveLength && Math.round(pointA.irradiance * 100) === Math.round(pointB.irradiance * 100);
    });
}

const useCreateLightRecipeFromSpectrum = () => {
    const { handleSnackbar } = React.useContext(UxContext)!;
    const {
        pageState: { formData },
        pageActions
    } = usePageStore();

    const aggregatedSpectrogram = formData.aggregatedSpectrogram as SpectrogramPoint[] | undefined;

    async function findOrCreate(): Promise<{ recipe: LightRecipeItem; wasCreated: boolean } | null> {
        const recipes = await lediumApi.get<LightRecipeItem[]>('lightRecipes/');
        if (!recipes || !aggregatedSpectrogram) {
            return null;
        }

        const match = recipes.find((r) => r.spectrogram && spectrogramsMatch(r.spectrogram, aggregatedSpectrogram));

        if (match) {
            return { recipe: match, wasCreated: false };
        }

        const payload = {
            name: generateUniqueName(
                formData.name as string,
                recipes.map((r) => r.name)
            ),
            description: '',
            spectrogram: aggregatedSpectrogram,
            csvData: aggregatedSpectrogram.map((p) => ({ waveLength: p.waveLength, value: p.irradiance })),
            spectrogramRanges: formData.intensityRanges
        };

        const created = await lediumApi.put<LightRecipeItem>('lightRecipes', payload);
        if (!created) {
            handleSnackbar({ message: i18n('action_failed') as string, severity: 'error' });
            return null;
        }
        handleSnackbar({ message: i18n('light_recipe_created') as string });
        return { recipe: created, wasCreated: true };
    }

    function assignRecipe(recipe: LightRecipeItem) {
        pageActions.formData.updateFormItem({ lightRecipe: recipe });
    }

    return { findOrCreate, assignRecipe };
};

export default useCreateLightRecipeFromSpectrum;
